import { Link } from "@heroui/react";
import { Debug } from "@sc07-canvas/lib/debug";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  ChevronDown,
  Cog,
  Crop,
  Ellipsis,
  Eye,
  Grid2x2,
  Hammer,
  Info,
  Scan,
  Settings,
  Share2,
  Wrench,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useAppContext } from "@/contexts/AppContext";
import { useCaptureContext } from "@/contexts/CaptureContext";
import { useDialog } from "@/contexts/DialogContext";
import { usePanel } from "@/contexts/PanelContext";

import { useHasRole } from "../../hooks/useHasRole";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useModerator } from "../../Moderator/Moderator";
import OpenChatButton from "../Chat/OpenChatButton";
import { Button } from "../core/Button";
import { SIDEBAR_SPRING } from "../SidebarBase";
import { ThemeSwitcher } from "./ThemeSwitcher";
import { User } from "./User";
import { useSidebarWidth } from "./useSidebarWidth";

export const HeaderRight = () => {
  const reduceMotion = useReducedMotion() ?? false;
  const { state: moderatorState, dispatch } = useModerator();
  const Panel = usePanel();
  const Dialog = useDialog();
  const { settings } = useAppContext();
  const compactButtons = settings.get["ui.compactButtons"];
  const compactThemeSwitcher =
    compactButtons || settings.get["ui.compactThemeSwitcher"];
  const headerActionClass = `header-action-button${compactButtons ? " header-action-button--compact" : ""}`;
  const sidebarWidth = useSidebarWidth("Right");
  const isMobile = useIsMobile();
  const offsetForSidebar = Boolean(
    !isMobile &&
      (Panel.state.settings || Panel.state.whois) &&
      settings.get["ui.adaptElementsToSidebar"],
  );
  const {
    captureCanvas,
    captureView,
    captureRegion,
    captureTemplateArea,
    hasTemplateCapture,
  } = useCaptureContext();
  const hasAdmin = useHasRole("ADMIN");
  const hasMod = useHasRole("MOD");
  const captureMenuRef = useRef<HTMLDivElement | null>(null);
  const moreMenuRef = useRef<HTMLDivElement | null>(null);
  const [captureMenuOpen, setCaptureMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  useEffect(() => {
    if (!captureMenuOpen && !moreMenuOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        (captureMenuRef.current?.contains(event.target) ||
          moreMenuRef.current?.contains(event.target))
      ) {
        return;
      }

      setCaptureMenuOpen(false);
      setMoreMenuOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setCaptureMenuOpen(false);
        setMoreMenuOpen(false);
      }
    };

    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [captureMenuOpen, moreMenuOpen]);

  const handleCaptureCanvas = () => {
    setCaptureMenuOpen(false);
    captureCanvas();
  };

  const handleCaptureView = () => {
    setCaptureMenuOpen(false);
    captureView();
  };

  const handleCaptureRegion = () => {
    setCaptureMenuOpen(false);
    captureRegion();
  };

  const handleCaptureTemplateArea = () => {
    setCaptureMenuOpen(false);
    captureTemplateArea();
  };

  return (
    <motion.div
      animate={{ x: offsetForSidebar ? -sidebarWidth : 0 }}
      className="header-right box flex flex-col gap-2"
      transition={reduceMotion ? { duration: 0 } : SIDEBAR_SPRING}
    >
      {!isMobile && <User />}
      <div className="header-right-actions flex gap-2">
        <div ref={captureMenuRef} className="header-share-menu relative">
          <Button
            aria-label="Share"
            aria-expanded={captureMenuOpen}
            aria-haspopup="menu"
            className={headerActionClass}
            onPress={() => {
              setMoreMenuOpen(false);
              setCaptureMenuOpen((open) => !open);
            }}
          >
            <Share2 aria-hidden="true" size={18} />
            <span className="header-button-label header-button-label--share">
              <span>Share</span>
              <ChevronDown
                aria-hidden="true"
                className={`transition-transform motion-reduce:transition-none ${captureMenuOpen ? "rotate-180" : ""}`}
                size={16}
              />
            </span>
          </Button>
          <AnimatePresence>
            {captureMenuOpen && (
              <motion.div
                animate={{ opacity: 1, scale: 1, y: 0 }}
                aria-label="Capture options"
                className="absolute right-0 top-[calc(100%+0.5rem)] z-50 flex min-w-52 flex-col gap-1"
                exit={{ opacity: 0, scale: 0.98, y: -4 }}
                initial={{ opacity: 0, scale: 0.98, y: -4 }}
                role="menu"
                transition={{ duration: reduceMotion ? 0 : 0.14 }}
              >
                <Button
                  className="justify-start"
                  onPress={() => {
                    Dialog.dispatch({
                      action: "OPEN_DIALOG",
                      dialog: { id: "SHARE_MODAL" },
                    });
                  }}
                  role="menuitem"
                  size="sm"
                >
                  <Share2 aria-hidden="true" size={16} />
                  <p>Share Link</p>
                </Button>
                <Button
                  className="justify-start"
                  onPress={handleCaptureCanvas}
                  silent
                  role="menuitem"
                  size="sm"
                >
                  <Grid2x2 aria-hidden="true" size={16} />
                  <p>Capture Canvas</p>
                </Button>
                <Button
                  className="justify-start"
                  onPress={handleCaptureView}
                  silent
                  role="menuitem"
                  size="sm"
                >
                  <Eye aria-hidden="true" size={16} />
                  <p>Capture View</p>
                </Button>
                <Button
                  className="justify-start"
                  onPress={handleCaptureRegion}
                  role="menuitem"
                  size="sm"
                >
                  <Scan aria-hidden="true" size={16} />
                  <p>Capture Region</p>
                </Button>
                {hasTemplateCapture && (
                  <Button
                    className="justify-start"
                    onPress={handleCaptureTemplateArea}
                    silent
                    role="menuitem"
                    size="sm"
                  >
                    <Crop aria-hidden="true" size={16} />
                    <p>Capture Template Area</p>
                  </Button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <Button
          aria-label="Settings"
          aria-pressed={Boolean(Panel.state.settings)}
          className={`header-mobile-overflow-action ${headerActionClass}${Panel.state.settings ? " is-active" : ""}`}
          onPress={() => {
            Panel.dispatch([
              "settings",
              Panel.state.settings ? undefined : true,
            ]);
          }}
        >
          <Settings aria-hidden="true" size={18} />
          <p className="header-button-label">Settings</p>
        </Button>
        <div className="header-lower-priority">
          {settings.get["ui.showThemeSwitcher"] && (
            <ThemeSwitcher compact={compactThemeSwitcher} />
          )}
          {hasMod && (
            <Button
              aria-pressed={Boolean(
                moderatorState.showMenu || moderatorState.inspectUser,
              )}
              className={`${headerActionClass}${
                moderatorState.showMenu || moderatorState.inspectUser
                  ? " is-active"
                  : ""
              }`}
              onPress={() => dispatch({ action: "OPEN_MENU" })}
            >
              <Hammer aria-hidden="true" size={18} />
              <p className="header-button-label header-button-label--short">
                Mod
              </p>
            </Button>
          )}
          {(hasAdmin || hasMod) && (
            <Button
              className={headerActionClass}
              href="/admin"
              target="_blank"
              as={Link}
            >
              <Cog aria-hidden="true" size={18} />
              <p className="header-button-label header-button-label--short">
                Admin
              </p>
            </Button>
          )}
          <OpenChatButton compact={compactButtons} />
        </div>
        <div ref={moreMenuRef} className="header-more-menu relative">
          <Button
            aria-expanded={moreMenuOpen}
            aria-haspopup="menu"
            aria-label="More"
            className={`${headerActionClass}${moreMenuOpen ? " is-active" : ""}`}
            onPress={() => {
              setCaptureMenuOpen(false);
              setMoreMenuOpen((open) => !open);
            }}
          >
            <Ellipsis aria-hidden="true" size={18} />
            <p className="header-button-label header-button-label--short">More</p>
          </Button>
          <AnimatePresence>
            {moreMenuOpen && (
              <motion.div
                animate={{ opacity: 1, scale: 1, y: 0 }}
                aria-label="More actions"
                className="absolute right-0 top-[calc(100%+0.5rem)] z-50 flex min-w-52 flex-col gap-1"
                exit={{ opacity: 0, scale: 0.98, y: -4 }}
                initial={{ opacity: 0, scale: 0.98, y: -4 }}
                role="menu"
                transition={{ duration: reduceMotion ? 0 : 0.14 }}
              >
                <Button
                  aria-pressed={Boolean(Panel.state.info)}
                  className="header-mobile-only-action justify-start"
                  onPress={() => {
                    setMoreMenuOpen(false);
                    Panel.dispatch([
                      "info",
                      Panel.state.info ? undefined : true,
                    ]);
                  }}
                  role="menuitem"
                  size="sm"
                >
                  <Info aria-hidden="true" size={16} />
                  <p>Info</p>
                </Button>
                {import.meta.env.DEV && (
                  <Button
                    className="header-mobile-only-action justify-start"
                    onPress={() => {
                      setMoreMenuOpen(false);
                      Debug.openDebugTools();
                    }}
                    role="menuitem"
                    size="sm"
                  >
                    <Wrench aria-hidden="true" size={16} />
                    <p>Debug Tools</p>
                  </Button>
                )}
                <Button
                  aria-pressed={Boolean(Panel.state.settings)}
                  className="header-mobile-only-action justify-start"
                  onPress={() => {
                    setMoreMenuOpen(false);
                    Panel.dispatch([
                      "settings",
                      Panel.state.settings ? undefined : true,
                    ]);
                  }}
                  role="menuitem"
                  size="sm"
                >
                  <Settings aria-hidden="true" size={16} />
                  <p>Settings</p>
                </Button>
                {settings.get["ui.showThemeSwitcher"] && (
                  <ThemeSwitcher
                    className="justify-start"
                    onPress={() => setMoreMenuOpen(false)}
                    role="menuitem"
                    size="sm"
                  />
                )}
                <OpenChatButton
                  className="justify-start"
                  onPress={() => setMoreMenuOpen(false)}
                  role="menuitem"
                  size="sm"
                />
                {hasMod && (
                  <Button
                    className="justify-start"
                    onPress={() => {
                      setMoreMenuOpen(false);
                      dispatch({ action: "OPEN_MENU" });
                    }}
                    role="menuitem"
                    size="sm"
                  >
                    <Hammer aria-hidden="true" size={16} />
                    <p>Mod</p>
                  </Button>
                )}
                {(hasAdmin || hasMod) && (
                  <Button
                    as={Link}
                    className="justify-start"
                    href="/admin"
                    onPress={() => setMoreMenuOpen(false)}
                    role="menuitem"
                    size="sm"
                    target="_blank"
                  >
                    <Cog aria-hidden="true" size={16} />
                    <p>Admin</p>
                  </Button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};
